import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import CardTitle from "@saleor/components/CardTitle";
import LocationRange from "@saleor/components/LocationRange";
import Money from "@saleor/components/Money";
import MoneyRange from "@saleor/components/MoneyRange";
import MoreActions from "@saleor/components/MoreActions";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import WeightRange from "@saleor/components/WeightRange";
import { ShippingZoneDetailsFragment_shippingMethods } from "@saleor/fragments/types/ShippingZoneDetailsFragment";
import {
  DeliveryTypeEnum,
  ShippingMethodStatus
} from "@saleor/types/globalTypes";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { maybe, renderCollection } from "../../../misc";
import { ICONBUTTON_SIZE } from "../../../theme";

const useStyles = makeStyles(
  theme => ({
    alignRight: {
      "&:last-child": {
        paddingRight: 0
      },
      paddingRight: 0,
      width: ICONBUTTON_SIZE + theme.spacing(0.5)
    },
    nameColumnText: {
      fontWeight: 600
    },
    nameColumnTextSub: {
      color: "#00C6A2",
      fontWeight: 550
    },
    tblCol: {
      minWidth: 300
    },
    tblColFixWidth: {
      width: 100
    }
  }),
  { name: "ShippingZoneRates" }
);

export interface ShippingZoneRatesProps {
  disabled: boolean;
  rates: ShippingZoneDetailsFragment_shippingMethods[];
  variant: "price" | "weight" | "location";
  shippingZoneId: string;
  onRateAdd: () => void;
  onRateEdit: (id: string) => void;
  onRateRemove: (id: string) => void;
  onSetAsActive: (data: any, shippingZoneId: string) => void;
}

const ShippingZoneRates: React.FC<ShippingZoneRatesProps> = props => {
  const {
    disabled,
    shippingZoneId,
    onRateAdd,
    onRateEdit,
    onRateRemove,
    onSetAsActive,
    rates,
    variant
  } = props;

  const classes = useStyles(props);
  const intl = useIntl();

  const activeRates = !!rates
    ? rates.filter(r => r.status === ShippingMethodStatus.ACT)
    : [];

  const getTitle = () => {
    switch (variant) {
      case "price":
        return intl.formatMessage({
          defaultMessage: "Price Based Rates",
          description: "price based shipping methods, section header"
        });
      case "location":
        return intl.formatMessage({
          defaultMessage: "Location Range Rates",
          description: "location based shipping methods, section header"
        });
      case "weight":
        return intl.formatMessage({
          defaultMessage: "Weight Based Rates",
          description: "weight based shipping methods, section header"
        });
      default:
        return "";
    }
  };

  const getValueColumnHeader = () => {
    switch (variant) {
      case "price":
        return intl.formatMessage({
          defaultMessage: "Value Range",
          description: "shipping method price range"
        });
      case "location":
        return intl.formatMessage({
          defaultMessage: "Kilometer Range",
          description: "shipping method kilometer range"
        });
      case "weight":
        return intl.formatMessage({
          defaultMessage: "Weight Range",
          description: "shipping method weight range"
        });
      default:
        return "";
    }
  };

  const getValueColumn = (
    rate: ShippingZoneDetailsFragment_shippingMethods
  ) => {
    if (variant === "location") {
      const filteredPrices = rate?.distancePrices || [];
      if (filteredPrices.length === 0) {
        return null;
      }

      const minFromKm = filteredPrices.reduce((min, dp) => {
        const fromKm = parseFloat(dp.fromKm.toString());
        return fromKm < min ? fromKm : min;
      }, Infinity);
      const maxToKm = filteredPrices.reduce((max, dp) => {
        const toKm = parseFloat(dp.toKm.toString());
        return toKm > max ? toKm : max;
      }, -Infinity);

      return (
        <LocationRange from={minFromKm.toFixed(2)} to={maxToKm.toFixed(2)} />
      );
    }

    switch (variant) {
      case "price":
        return (
          <MoneyRange
            from={rate.minimumOrderPrice}
            to={rate.maximumOrderPrice}
          />
        );
      case "weight":
        return (
          <WeightRange
            from={rate.minimumOrderWeight}
            to={rate.maximumOrderWeight}
          />
        );
      default:
        return <></>;
    }
  };

  const getDelTypeDisplay = delType => {
    switch (delType) {
      case DeliveryTypeEnum.EXPRESS:
        return "Express Delivery";
      case DeliveryTypeEnum.IN_STORE_PICKUP:
        return "In-Store Pickup";
      case DeliveryTypeEnum.NEXT_DAY:
        return "Next Day Delivery";
      case DeliveryTypeEnum.SAME_DAY:
        return "Same Day Delivery";
      case DeliveryTypeEnum.EXPRESS_LONG_DISTANCE:
        return "Express-Long Distance Delivery";
      case DeliveryTypeEnum.NATIONWIDE_DELIVERY:
        return "Nationwide Delivery";
      default:
        return "";
    }
  };

  const getStatusDisplay = status => {
    switch (status) {
      case ShippingMethodStatus.ACT:
        return "Active";
      default:
        return "";
    }
  };

  return (
    <Card>
      <CardTitle
        height="const"
        title={getTitle()}
        toolbar={
          <Button color="primary" disabled={disabled} onClick={onRateAdd}>
            <FormattedMessage
              defaultMessage="Create rate"
              description="button"
            />
          </Button>
        }
      />
      <ResponsiveTable>
        <TableHead>
          <TableRow>
            <TableCell className={classes.tblCol}>
              <FormattedMessage
                defaultMessage="Delivery Type"
                description="shipping method delivery type"
              />
            </TableCell>
            <TableCell className={classes.tblCol}>
              <FormattedMessage
                defaultMessage="Rate Name"
                description="shipping method rate name"
              />
            </TableCell>
            <TableCell className={classes.tblCol}>
              {getValueColumnHeader()}
            </TableCell>
            <TableCell className={classes.tblColFixWidth}>
              {variant !== "location" && (
                <FormattedMessage
                  defaultMessage="Price"
                  description="shipping method price"
                />
              )}
            </TableCell>
            <TableCell className={classes.tblColFixWidth} />
          </TableRow>
        </TableHead>
        <TableBody>
          {renderCollection(
            rates,
            rate => (
              <TableRow hover={!!rate} key={rate ? rate.id : "skeleton"}>
                <TableCell className={classes.tblCol}>
                  <div className={classes.nameColumnText}>
                    {maybe<React.ReactNode>(
                      () => getDelTypeDisplay(rate.deliveryType),
                      <Skeleton />
                    )}
                  </div>
                  <div className={classes.nameColumnTextSub}>
                    <small>
                      {maybe<React.ReactNode>(
                        () => getStatusDisplay(rate.status),
                        null
                      )}
                    </small>
                  </div>
                </TableCell>
                <TableCell className={classes.tblCol}>
                  {maybe<React.ReactNode>(() => rate.name, <Skeleton />)}
                </TableCell>
                <TableCell className={classes.tblCol}>
                  {maybe<React.ReactNode>(
                    () => getValueColumn(rate),
                    <Skeleton />
                  )}
                </TableCell>

                <TableCell className={classes.tblColFixWidth}>
                  {variant !== "location" &&
                    maybe<React.ReactNode>(
                      () => <Money money={rate.price} />,
                      <Skeleton />
                    )}
                </TableCell>
                <TableCell className={classes.tblColFixWidth}>
                  {!!rate && rate?.id && (
                    <MoreActions
                      id={"shippingZoneRatesMoreActions" + rate?.id}
                      items={[
                        {
                          action: () => onSetAsActive(rate, shippingZoneId),
                          disabled: activeRates.length === 1,
                          id: "shippingZoneRatesSetActive",
                          label:
                            rate?.status === ShippingMethodStatus.INACTIVE
                              ? "Set as Inactive"
                              : "Set as Active",
                          tooltip: "At least one active rate is required."
                        },
                        {
                          action: () => onRateEdit(rate.id),
                          id: "shippingZoneRatesEdit",
                          label: "Edit"
                        },
                        {
                          action: () => onRateRemove(rate?.id || ""),
                          disabled: rate?.status === ShippingMethodStatus.ACT,
                          id: "shippingZoneRatesDelete",
                          label: "Delete",
                          tooltip:
                            "This rate is currently active and cannot be deleted."
                        }
                      ]}
                    />
                  )}
                </TableCell>
              </TableRow>
            ),
            () => (
              <TableRow>
                <TableCell colSpan={4}>
                  <FormattedMessage defaultMessage="No shipping rates found" />
                </TableCell>
              </TableRow>
            )
          )}
        </TableBody>
      </ResponsiveTable>
    </Card>
  );
};
ShippingZoneRates.displayName = "ShippingZoneRates";
export default ShippingZoneRates;
